import React, { SyntheticEvent, useCallback, useMemo } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { Field, Formik } from 'formik';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { centsToDollarString } from 'utils';

import { IFormProps, Omit } from '@rbi-ctg/frontend';
import ActionButton from 'components/action-button';
import { CountrySelect } from 'components/country-select';
import { useBirthdayIsRequired } from 'components/floating-label-fields/birthday/use-birthday-is-required';
import { FormikFloatingLabelPhoneInput } from 'components/floating-label-fields/floating-label-phone-input';
import { FormikBirthdayInput } from 'components/formik/birthday-input';
import { FormikTextInput } from 'components/formik/text-input';
import CompletePhoneFromBrazeId from 'components/phone-from-braze-id/complete-phone-from-braze-id';
import { IsoCountryCode } from 'generated/rbi-graphql';
import { useLoyaltyContent } from 'hooks/loyalty/use-loyalty-content';
import useDialogModal from 'hooks/use-dialog-modal';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { useLocale } from 'state/intl';
import { REGIONS } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  MyAccountFieldsVariations,
  defaultMyAccountFieldsVariation,
} from 'state/launchdarkly/variations';
import { useLocationContext } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames } from 'state/mParticle/constants';
import { PREPAID_RELOAD_AMOUNTS } from 'state/payment/constants';
import { useUIContext } from 'state/ui';
import {
  POSSIBLE_DEFAULT_OR_EMPTY_DOB_VALUES,
  formatDateObject as dobObjToString,
  dobStringToObj,
  sanitizePhoneNumber,
} from 'utils/form';
import { ISOs, defaultLocaleKeys, postalCodeStringLocaleKey } from 'utils/form/constants';
import { IDateOfBirth } from 'utils/form/types';
import { validationConfig } from 'utils/form/user-profile-validation-config/validation';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

import { FormField } from './form-field';
import { getFormErrors } from './get-form-errors';
import PromotionalEmailsCheckbox from './promotional-email-checkbox';
import ReloadButton from './reload-button';
import ReloadText from './reload-text';
import { TermsSection } from './terms-section';
import { IUserFormData } from './types';

const ReloadButtonContainer = styled.div`
  display: flex;
  border: 1px solid ${Styles.color.grey.four};
  border-radius: 4px;
  margin: 10px 0;
`;

const InputContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const RemoveBirthdayButton = styled.a`
  position: absolute;
  right: 0;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  margin-right: 1.4rem;

  ${Styles.desktop`
    margin-right: 0;
  `}
`;

const RemoveBirthdayModalContent = styled.div`
  margin: 1.5rem 0;
`;

interface IUserInfoFormProps extends IFormProps<Omit<IUserFormData, 'email'>> {
  readOnly?: boolean;
  user: UserDetails | null;
}

function UserInfoForm({ onSubmit, readOnly = false, user }: IUserInfoFormProps) {
  const enableNewPhoneField = useFlag(LaunchDarklyFlag.ENABLE_NEW_PHONE_NUMBER_FIELD);
  const enablePhoneNumberEditing = useFlag(
    LaunchDarklyFlag.ENABLE_CUSTOMER_CAN_EDIT_OWN_PHONE_NUMBER
  );
  const myAccountFieldsVariations =
    useFlag<MyAccountFieldsVariations>(LaunchDarklyFlag.MY_ACCOUNT_FIELDS_VARIATIONS) ||
    defaultMyAccountFieldsVariation;
  const enableNumericPhoneValidation = useFlag(LaunchDarklyFlag.ENABLE_NUMERIC_PHONE_VALIDATION);
  const enableRemoveDateOfBirth = useFlag(
    LaunchDarklyFlag.ENABLE_REMOVE_DATE_OF_BIRTH_IN_ACCOUNT_PAGE
  );
  const { formatMessage } = useIntl();
  const { region } = useLocale() as { region: REGIONS };
  const { navigate } = useLocationContext();
  const { currencyIsSuffix } = useUIContext();
  const mParticle = useMParticleContext();
  const currencySymbol = '$';

  const { birthdayLegalText } = useLoyaltyContent();

  const [BirthdayModal, openBirthdayModal] = useDialogModal({
    modalAppearanceEventMessage: 'Delete Date of Birth',
    showCancel: true,
    onConfirm: (params: any) => {
      const { handleSubmission, setFieldValue } = params;
      setFieldValue('dob', '');
      setFieldValue('dobDeleted', true);
      handleSubmission();
      mParticle.logEvent(CustomEventNames.UPDATE_USER_ATTRIBUTES, EventTypes.Navigation, {
        component: ClickEventComponentNames.BUTTON,
        text: 'Remove Date of Birth',
      });
    },
  });

  const DOB_DEFAULT_OR_EMPTY = useMemo(
    () => POSSIBLE_DEFAULT_OR_EMPTY_DOB_VALUES.some(v => v === user?.details?.dob || ''),
    [user]
  );

  const initialValues: IUserFormData = useMemo(() => {
    // dob will come in as string so let's convert it
    const dob = dobStringToObj(user?.details?.dob);
    return {
      agreesToTermsOfService: true,
      /**
       * NOTE: this is a workaround because Typescript doesn't like that we are
       *       converting the dob into an object for the form values and changing
       *       the interface itself causes numerous cascading type errors.
       */
      dob: dob as unknown as string,
      email: user?.details?.email || '',
      name: user?.details?.name || '',
      phoneNumber: user?.details?.phoneNumber || '',
      promotionalEmails: !!user?.details?.promotionalEmails || false,
      isoCountryCode: (user?.details?.isoCountryCode as IsoCountryCode) || ISOs.USA,
      zipcode: user?.details?.zipcode || '',
      defaultReloadAmt: user?.details?.defaultReloadAmt || 500,
      defaultReloadPaymentMethodId: user?.details?.defaultPaymentAccountId || '',
      defaultCheckoutPaymentMethodId:
        user?.details?.defaultAccountIdentifier || user?.details?.defaultFdAccountId || '',
      autoReloadEnabled: user?.details?.autoReloadEnabled || false,
      autoReloadThreshold: user?.details?.autoReloadThreshold || 500,
    };
  }, [user]);

  const disablePhoneNumberField = readOnly || !enablePhoneNumberEditing;

  const handleSubmit = useCallback(
    async (formData: IUserFormData) => {
      // We can only send a string for DOB
      const dob =
        typeof formData.dob === 'object'
          ? // new DOB value - LD.ENABLE_CUSTOM_BIRTHDAY_INPUT
            dobObjToString(formData.dob as IDateOfBirth)
          : // Legacy DOB value
            formData.dob || '';
      const phoneNumber = await sanitizePhoneNumber(formData.phoneNumber, formData.isoCountryCode);
      return onSubmit({
        ...formData,
        phoneNumber,
        dob,
      }).then(() => {
        navigate(routes.account);
        toast.success(formatMessage({ id: 'successfullyUpdatedUserAccountInfo' }));
      });
    },
    [formatMessage, navigate, onSubmit]
  );

  const birthdayIsRequired = useBirthdayIsRequired(region, validationConfig);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={getFormErrors({
        formatMessage,
        region,
        myAccountFieldsVariations,
        simplePhoneValidation: enableNumericPhoneValidation,
      })}
      enableReinitialize
    >
      {({
        handleChange,
        handleBlur,
        handleSubmit: handleSubmission,
        values,
        setFieldValue,
        isValid,
        isSubmitting,
      }) => (
        <form onSubmit={handleSubmission}>
          <CompletePhoneFromBrazeId setFieldValue={setFieldValue} />
          <InputContainer>
            {myAccountFieldsVariations.email && (
              <Field
                name="email"
                component={FormikTextInput}
                data-testid="account-info-email-input"
                label={formatMessage({ id: 'yourEmail' })}
                required
                readOnly
              />
            )}
            {myAccountFieldsVariations.name && (
              <Field
                name="name"
                component={FormikTextInput}
                data-testid="account-info-name-input"
                label={formatMessage({ id: 'name' })}
                required
                disabled={readOnly}
                autoFocus
              />
            )}
            {myAccountFieldsVariations.phone &&
              (enableNewPhoneField ? (
                <Field
                  name="phoneNumber"
                  component={FormikFloatingLabelPhoneInput}
                  label={formatMessage({ id: 'phoneNumber' })}
                  readOnly={disablePhoneNumberField}
                />
              ) : (
                <FormField
                  name="phoneNumber"
                  component={FormikTextInput}
                  label={formatMessage({ id: 'phoneNumber' })}
                  type="tel"
                  initialValue={initialValues.phoneNumber}
                  disabled={disablePhoneNumberField && Boolean(initialValues.phoneNumber)}
                  data-testid="old-phone-number-field"
                />
              ))}
            {myAccountFieldsVariations.dob && (
              <>
                {enableRemoveDateOfBirth && (user?.details?.dob || '').length > 1 && (
                  <>
                    <RemoveBirthdayButton
                      onClick={() =>
                        openBirthdayModal({
                          title: formatMessage({ id: 'removeDateOfBirth' }),
                          handleSubmission,
                          setFieldValue,
                        })
                      }
                    >
                      {formatMessage({ id: 'removeDateOfBirth' })}
                    </RemoveBirthdayButton>
                    <BirthdayModal
                      bodyComponent={
                        <RemoveBirthdayModalContent>
                          {formatMessage({ id: 'removeDateOfBirthModalContent' })}
                        </RemoveBirthdayModalContent>
                      }
                      buttonLabel={formatMessage({ id: 'removeDateOfBirthConfirm' })}
                    />
                  </>
                )}
                <FormField
                  component={FormikBirthdayInput}
                  disabled={readOnly || !DOB_DEFAULT_OR_EMPTY || user?.details?.dobDeleted}
                  onBlur={handleBlur}
                  hintMessage={birthdayLegalText ?? ''}
                  required={birthdayIsRequired}
                />
              </>
            )}
            {myAccountFieldsVariations.country && (
              <CountrySelect
                name="isoCountryCode"
                label={formatMessage({ id: 'country' })}
                value={values.isoCountryCode}
                onChange={handleChange}
                disabled
              />
            )}
            {myAccountFieldsVariations.zip && (
              <Field
                name="zipcode"
                component={FormikTextInput}
                data-testid="account-info-zip-input"
                label={formatMessage({
                  id:
                    postalCodeStringLocaleKey[values.isoCountryCode] ??
                    defaultLocaleKeys.postalCode,
                })}
                disabled={readOnly}
                maxLength={10}
                onChange={(event: SyntheticEvent<HTMLInputElement>) =>
                  setFieldValue('zipcode', event.currentTarget.value.toUpperCase())
                }
              />
            )}
          </InputContainer>
          <ReloadText />
          <ReloadButtonContainer>
            {PREPAID_RELOAD_AMOUNTS.map(amount => (
              <Field
                key={`reload-amount-${amount}`}
                name="defaultReloadAmt"
                component={ReloadButton}
                disabled={readOnly}
                selected={values.defaultReloadAmt === amount}
                onClick={() => setFieldValue('defaultReloadAmt', amount)}
              >
                {!currencyIsSuffix && currencySymbol}
                {centsToDollarString(amount, 0)}
                {currencyIsSuffix && currencySymbol}
              </Field>
            ))}
          </ReloadButtonContainer>
          <PromotionalEmailsCheckbox
            checked={values.promotionalEmails}
            onChange={handleChange}
            disabled={readOnly}
          />
          <Box margin="1.875rem 0 0">
            <ActionButton
              data-testid="account-info-form-submit-button"
              fullWidth
              type="submit"
              disabled={readOnly || !isValid || isSubmitting}
              isLoading={isSubmitting}
            >
              {formatMessage({ id: 'update' })}
            </ActionButton>
          </Box>
          <TermsSection />
        </form>
      )}
    </Formik>
  );
}

export default UserInfoForm;
